import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AttachmentsField from "@/components/AttachmentsField";
import {
  Plus, ExternalLink, Copy, Pencil, Trash2, FileText, Search,
  FileCheck2, Landmark, Building2, Receipt, Briefcase, Shield, Files, ScrollText, Layers, Paperclip,
} from "lucide-react";

const CATEGORIES = [
  { key: "llp_agreement", label: "LLP Agreement", icon: ScrollText, color: "red" },
  { key: "gst", label: "GST", icon: Receipt, color: "blue" },
  { key: "pan", label: "PAN", icon: FileCheck2, color: "indigo" },
  { key: "bank", label: "Bank", icon: Landmark, color: "emerald" },
  { key: "investment_deeds", label: "Investment Deeds", icon: Briefcase, color: "amber" },
  { key: "loan_agreements", label: "Loan Agreements", icon: Files, color: "orange" },
  { key: "insurance", label: "Insurance", icon: Shield, color: "cyan" },
  { key: "statutory", label: "Statutory", icon: Building2, color: "slate" },
  { key: "other", label: "Other", icon: Layers, color: "zinc" },
];
const CAT_BY_KEY = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));
const CAT_CLS = {
  red: "bg-red-50 text-red-700 border-red-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
  slate: "bg-slate-50 text-slate-700 border-slate-200",
  zinc: "bg-zinc-50 text-zinc-700 border-zinc-200",
};

const emptyForm = () => ({ label: "", category: "other", url: "", note: "" });

export default function Documents() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const canEdit = user?.role === "admin" || user?.role === "accounts";
  const canDelete = user?.role === "admin";

  const load = () => api.get("/company-documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyForm()); setDlg(true); };
  const openEdit = (d) => { setEditing(d); setForm({ label: d.label, category: d.category, url: d.url, note: d.note || "" }); setDlg(true); };

  const save = async () => {
    if (!form.label) return toast.error("Label is required");
    const hasUrl = !!(form.url || "").trim();
    const hasFiles = editing && (editing.attachments || []).length > 0;
    if (!hasUrl && !hasFiles) return toast.error("Provide a URL or upload a file");
    try {
      if (editing) {
        await api.put(`/company-documents/${editing.id}`, form);
        toast.success("Document updated");
      } else {
        const r = await api.post("/company-documents", form);
        // stay in the dialog so the user can attach files without closing it
        setEditing(r.data);
        toast.success("Document saved — you can now attach files");
        await load();
        return;
      }
      setDlg(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.label}"?`)) return;
    try {
      await api.delete(`/company-documents/${d.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Copy failed"); }
  };

  const filtered = docs.filter((d) => {
    if (activeCat !== "all" && d.category !== activeCat) return false;
    if (q && !`${d.label} ${d.note}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const countByCat = docs.reduce((acc, d) => {
    acc[d.category] = (acc[d.category] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Company · Vault</div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="docs-title">
            <em className="italic">Documents.</em>
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-lg">
            Every important MARKSAM document — LLP agreement, GST, deeds, insurance — one central shelf.
            Paste a link or upload the file itself.
          </p>
        </div>
        {canEdit && (
          <Button onClick={openAdd} className="rounded-md bg-red-600 hover:bg-red-700 text-white h-9 gap-1.5" data-testid="add-doc">
            <Plus className="h-4 w-4" /> Add document
          </Button>
        )}
      </div>

      {/* Category chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setActiveCat("all")}
          data-testid="cat-all"
          className={`text-[12px] font-medium px-3 py-1.5 rounded-full border transition-colors ${
            activeCat === "all"
              ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
          }`}
        >
          All <span className="ml-1 text-[10px] font-mono-fin opacity-70">{docs.length}</span>
        </button>
        {CATEGORIES.map((c) => {
          const n = countByCat[c.key] || 0;
          const active = activeCat === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setActiveCat(c.key)}
              data-testid={`cat-${c.key}`}
              className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full border transition-colors ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              <c.icon className="h-3 w-3" /> {c.label}
              {n > 0 && <span className={`text-[10px] font-mono-fin ${active ? "opacity-70" : "text-slate-400"}`}>{n}</span>}
            </button>
          );
        })}
        <div className="flex-1 min-w-[120px]" />
        <div className="relative w-full sm:w-[240px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-8 h-9 text-[13px]" data-testid="docs-search" />
        </div>
      </div>

      {/* Doc list */}
      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white rounded-md p-12 text-center">
          <FileText className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <div className="text-[13px] text-slate-600 font-medium">No documents yet</div>
          <div className="text-[11px] text-slate-400 mt-1">
            {canEdit ? "Click 'Add document' to paste a link." : "Ask an admin to add one."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((d) => {
            const cat = CAT_BY_KEY[d.category] || CAT_BY_KEY.other;
            return (
              <div key={d.id} className="border border-slate-200 bg-white rounded-md p-4 flex items-start gap-3 hover:border-slate-300 transition-colors" data-testid={`doc-${d.id}`}>
                <div className={`h-9 w-9 rounded-md border shrink-0 flex items-center justify-center ${CAT_CLS[cat.color]}`}>
                  <cat.icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">{d.label}</div>
                    <span className={`inline-flex text-[9px] tracking-wider uppercase font-medium px-1.5 py-0.5 rounded border ${CAT_CLS[cat.color]}`}>
                      {cat.label}
                    </span>
                  </div>
                  {d.note && <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{d.note}</div>}
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    {d.url && (
                      <a href={d.url} target="_blank" rel="noopener noreferrer"
                        className="text-[11px] text-red-600 hover:underline inline-flex items-center gap-1"
                        data-testid={`doc-open-${d.id}`}>
                        <ExternalLink className="h-3 w-3" /> Open link
                      </a>
                    )}
                    {d.url && (
                      <button onClick={() => copy(d.url)} className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                        <Copy className="h-3 w-3" /> Copy
                      </button>
                    )}
                    {(d.attachments || []).length > 0 && (
                      <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                        <Paperclip className="h-3 w-3" /> {d.attachments.length} file{d.attachments.length > 1 ? "s" : ""}
                      </span>
                    )}
                    {canEdit && (
                      <button onClick={() => openEdit(d)} className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 ml-auto">
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => remove(d)} className="text-[11px] text-slate-400 hover:text-red-600 inline-flex items-center gap-1">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/edit dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit document" : "Add document"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Label <span className="text-red-600">*</span></Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1.5" placeholder="e.g. MARKSAM LLP Agreement 2024" data-testid="doc-label" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="doc-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>URL <span className="text-slate-400 text-[10px]">(optional if you upload files below)</span></Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-1.5" placeholder="https://drive.google.com/…" data-testid="doc-url" />
              <div className="text-[10px] text-slate-400 mt-1">Must start with http:// or https://</div>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" placeholder="Renewal date, contact person, etc." />
            </div>
            <div className="border-t border-slate-100 pt-3">
              <AttachmentsField
                parentId={editing?.id}
                attachments={editing?.attachments || []}
                onChange={(list) => {
                  setEditing((e) => (e ? { ...e, attachments: list } : e));
                  load();
                }}
                uploadUrl={(id) => `/company-documents/${id}/attachments`}
                deleteUrl={(id, aid) => `/company-documents/${id}/attachments/${aid}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} className="rounded-md h-9">
              {editing ? "Done" : "Cancel"}
            </Button>
            <Button onClick={save} className="rounded-md h-9 bg-red-600 hover:bg-red-700 text-white" data-testid="doc-save">
              {editing ? "Save changes" : "Create & attach files"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
